import React from 'react'
import Helmet from 'react-helmet'
import Link from 'gatsby-link'
import Layout from 'components/Layout'
import jungImg from 'static/jung.jpg'
import sungImg from 'static/sung.jpg'

const DoctorProfile = ({ id, image, name, title, paragraphs, reversed }) => (
  <section
    id={id}
    className={`py-24 px-6 ${reversed ? 'bg-offWhite' : 'bg-white'}`}
  >
    <div className="container mx-auto max-w-5xl">
      <div
        className={`flex flex-col ${
          reversed ? 'md:flex-row-reverse' : 'md:flex-row'
        } gap-12 items-start`}
      >
        {/* Photo Card */}
        <div style={{ flex: '0 0 auto' }}>
          <div
            className="rounded-xl overflow-hidden"
            style={{
              width: '320px',
              maxWidth: '100%',
              boxShadow:
                '0 7px 14px 0 rgba(50, 50, 93, 0.1), 0 3px 6px 0 rgba(0, 0, 0, 0.07)',
              border: '1px solid #e3e8ee'
            }}
          >
            {/* Gradient header */}
            <div
              className="relative"
              style={{
                height: '150px',
                background: 'linear-gradient(135deg, #635bff 0%, #11efe3 100%)'
              }}
            />

            {/* Overlapping profile image */}
            <div className="flex justify-center" style={{ marginTop: '-60px' }}>
              <div
                className="rounded-full overflow-hidden border-4 border-white"
                style={{
                  width: '120px',
                  height: '120px',
                  minWidth: '120px',
                  minHeight: '120px',
                  boxShadow: '0 4px 14px 0 rgba(50, 50, 93, 0.15)',
                  position: 'relative'
                }}
              >
                <img
                  src={image}
                  alt={name}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>

            {/* Name and title */}
            <div className="text-center" style={{ padding: '16px 24px 24px' }}>
              <h3 className="text-xl font-semibold text-navy mb-1">{name}</h3>
              <p className="text-sm font-medium" style={{ color: '#635bff' }}>
                {title}
              </p>
            </div>
          </div>
        </div>

        {/* Bio Content */}
        <div style={{ flex: '1 1 0%', minWidth: 0 }}>
          {paragraphs.map((text, i) => (
            <p
              key={i}
              className="text-lg text-textSecondary leading-relaxed"
              style={{
                marginBottom: i < paragraphs.length - 1 ? '1.25rem' : 0
              }}
            >
              {text}
            </p>
          ))}
        </div>
      </div>
    </div>
  </section>
)

const AboutUsPage = () => (
  <Layout>
    <Helmet
      title="About Our Dentists | Oregon City Smiles"
      meta={[
        {
          name: 'description',
          content:
            'Meet Dr. Sung Yeon Ji and Dr. Jung Hun Ji, experienced family dentists in Oregon City, OR. Learn about our team, qualifications, and commitment to quality dental care.'
        },
        {
          property: 'og:title',
          content: 'About Our Dentists | Oregon City Smiles'
        },
        {
          property: 'og:description',
          content:
            'Meet Dr. Sung Yeon Ji and Dr. Jung Hun Ji, experienced family dentists in Oregon City, OR.'
        }
      ]}
    />

    {/* Hero Section */}
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #0a2540 0%, #1a1f36 50%, #635bff 100%)',
        minHeight: '340px'
      }}
    >
      {/* Gradient Mesh Background */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at 20% 50%, #11efe3 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, #ff80b5 0%, transparent 50%), radial-gradient(ellipse at 40% 20%, #635bff 0%, transparent 40%)'
        }}
      />

      <div
        className="relative z-10 container mx-auto px-6 flex flex-col items-center justify-center text-center"
        style={{
          minHeight: '340px',
          paddingTop: '5rem',
          paddingBottom: '5rem'
        }}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
          style={{
            background:
              'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          Our Team
        </div>

        <h1
          className="text-white font-bold mb-4 leading-tight"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            letterSpacing: '-0.03em'
          }}
        >
          Meet the Oregon City Smiles Team
        </h1>

        <p
          className="text-lg leading-relaxed max-w-2xl mx-auto"
          style={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          Our experienced dentists are dedicated to providing you with the
          highest quality dental care in a comfortable, welcoming environment.
        </p>
      </div>
    </section>

    {/* Dr. Sung Section */}
    <DoctorProfile
      id="sung"
      image={sungImg}
      name="Dr. Sung Yeon Ji"
      title="Doctor of Dental Surgery"
      reversed={false}
      paragraphs={[
        'Dr. Sung Yeon Ji, DDS (DR. SUNG), received her DDS from New York University College of Dentistry in 2013. She has been working as a general dentist in private practices. Dr. Sung believes in building long term relationships with her patients in a gentle and comfortable environment. She is innovative, proactive and comprehensive in her approach to dental care for her patients.',
        'Patient centered care is always the priority and you are always in control when it comes to your dental treatments. Dr. Sung will take the time you need to understand all aspects of your dental care.',
        'Dr. Ji values building long term relationships with her patients and providing quality dentistry in a gentle, comfortable environment. She became interested in dentistry in her teens, she loves her profession more and more everyday.'
      ]}
    />

    {/* Dr. Jung Section */}
    <DoctorProfile
      id="jung"
      image={jungImg}
      name="Dr. Jung Hun Ji"
      title="Doctor of Dental Surgery"
      reversed={true}
      paragraphs={[
        'Dr. Jung Hun Ji (DR. JI) graduated from Loma Linda University with Doctorate of Dental Surgery in 2014. He started practicing general dentistry in Reno, Nevada, after graduating dental school. After working in Reno for few years, he decided to move back to Oregon where his family is. He has been practicing in the area as an associate before he started this private practice.',
        'Dr. Ji regularly pursues continuing education courses in Oral surgery, Endodontics, Periodontics, Dental implants and more to stay on the cutting edge. Dr. Ji is a proud member of American dental association, Oregon Dental Association and Clackamas County Dental Society.',
        "Drs. Ji's family moved to Oregon from South Korea in their teens. Both Dr. Sung and Dr. Ji grew up in Eugene, Oregon and graduated from University of Oregon, both are huge Oregon Ducks' fans. In his spare time, Dr. Ji enjoys hiking, watching movies, and snowboarding."
      ]}
    />

    {/* CTA Section */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto text-center max-w-3xl">
        {/* Decorative line */}
        <div
          className="mx-auto mb-8 rounded-full"
          style={{
            width: '64px',
            height: '4px',
            background: 'linear-gradient(90deg, #635bff 0%, #11efe3 100%)'
          }}
        />

        <h2 className="text-4xl font-bold text-navy mb-4 leading-tight">
          Ready to Meet Us?
        </h2>

        <p className="text-lg text-textSecondary leading-relaxed mb-10 max-w-2xl mx-auto">
          Schedule an appointment and experience the difference of personalized,
          patient-centered dental care.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/appointments"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold text-base rounded-lg transition-all duration-150 hover:bg-primaryDark"
            style={{
              boxShadow: '0 4px 14px 0 rgba(99, 91, 255, 0.39)'
            }}
          >
            Schedule an Appointment
          </Link>
          <Link
            to="/treatments"
            className="inline-flex items-center justify-center px-8 py-4 font-semibold text-base rounded-lg transition-all duration-150 text-textPrimary hover:bg-offWhite"
            style={{
              border: '1px solid #e3e8ee'
            }}
          >
            View Our Services
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  </Layout>
)

export default AboutUsPage
